/**
 * ExportPanel - 지도 내보내기 설정 패널 (확장 버전)
 * 제목, 축척, 방위표, 범례, 텍스트 박스 기능 포함
 * 드래그로 요소 위치 조정 및 텍스트 스타일링 지원
 */

import { exportTool } from '../../tools/ExportTool.js';
import { mapManager } from '../../core/MapManager.js';
import { layerManager } from '../../core/LayerManager.js';
import { buildLegendModel } from '../../tools/legendModel.js';
import { applyLegendVisibility } from '../../tools/legendVisibility.js';

class ExportPanel {
  constructor() {
    this.modal = null;
    this.previewCanvas = null;
    this.previewCtx = null;
    this.dragging = null;
    this.dragOffset = { x: 0, y: 0 };
    this.includeBasemap = true;
    this.mapPreviewCanvas = null;
    this.mapPreviewBasemap = null;
    this.capturingPreview = false;
    this.mapElements = {
      showLegend: true,
      showScaleBar: true,
      legendFontSize: 12
    };
    this.previewZoom = 1;

    // 기본 위치 및 스타일 설정
    this.elements = {
      title: {
        enabled: false,
        text: '지도 제목',
        x: 0.5,
        y: 0.08,
        fontSize: 24,
        fontWeight: 'bold',
        fontFamily: 'Malgun Gothic',
        color: '#333333',
        background: true,
        backgroundColor: '#ffffff',
        backgroundOpacity: 0.85,
        shadow: false,
        shadowColor: '#000000',
        stroke: false,
        strokeColor: '#ffffff',
        strokeWidth: 2
      },
      compass: {
        enabled: true,
        x: 0.92,
        y: 0.12,
        size: 50,
        style: 'basic',
        imageSrc: null,   // 커스텀 이미지 data URL
        image: null,      // 로드된 Image 객체 (런타임)
        imageName: ''
      },
      legend: {
        enabled: false,
        // 방위표(0.92, 0.12)와 겹치지 않는 오른쪽 중간. 박스 왼쪽 위 모서리 기준.
        x: 0.78,
        y: 0.5,
        headerText: '범례',
        showHeader: true,
        // 범례에 넣을 레이어 id. null이면 "아직 고른 적 없음" → 보이는 레이어를 기본값으로.
        selectedLayerIds: null,
        fontSize: 12,
        fontFamily: 'Malgun Gothic',
        color: '#333333',
        background: true,
        backgroundColor: '#ffffff',
        backgroundOpacity: 0.9
      },
      textBox: {
        enabled: false,
        text: '',
        x: 0.5,
        y: 0.85,
        fontSize: 12,
        fontWeight: 'normal',
        fontFamily: 'Malgun Gothic',
        color: '#333333',
        background: true,
        backgroundColor: '#ffffff',
        backgroundOpacity: 0.9,
        shadow: false,
        stroke: false,
        strokeColor: '#ffffff',
        strokeWidth: 1
      }
    };

    this.fonts = [
      { value: 'Malgun Gothic', label: '맑은 고딕' },
      { value: 'Nanum Gothic', label: '나눔고딕' },
      { value: 'Noto Sans KR', label: 'Noto Sans' },
      { value: 'Arial', label: 'Arial' },
      { value: 'Georgia', label: 'Georgia' },
      { value: 'Times New Roman', label: 'Times' }
    ];
  }

  show() {
    this.render();
    this.applyMapElements();
    this.refreshMapCapture();
  }

  /**
   * 지도 위 범례·축척바 DOM에 현재 설정 반영
   * (위치는 사용자가 지도에서 직접 드래그로 조정)
   */
  applyMapElements() {
    const legendSelector = '.choropleth-legend, .chart-map-legend, .heatmap-legend, .cartogram-legend';
    const legends = document.querySelectorAll(legendSelector);
    legends.forEach(el => {
      el.style.fontSize = this.mapElements.legendFontSize + 'px';
    });
    // '범례 표시'는 켜진 레이어의 범례만 다시 보인다 — 숨긴 레이어의 범례까지 되살리면 안 된다
    for (const [layerId, layerInfo] of layerManager.layers) {
      applyLegendVisibility(layerId, this.mapElements.showLegend && layerInfo.visible !== false);
    }

    const scaleBars = document.querySelectorAll('.map-scale-bar, .ol-scale-line');
    scaleBars.forEach(el => {
      el.style.display = this.mapElements.showScaleBar ? '' : 'none';
    });
  }

  /**
   * 범례에 넣을 수 있는 레이어 — 레이어 창과 같은 순서.
   * 범례 기호로 요약할 수 없는 레이어(래스터·히트맵·도형표현도)는 buildLegendModel이
   * null을 내므로 여기서 빠진다.
   */
  legendCandidates() {
    return layerManager.getAllLayers()
      .map(layerInfo => ({ layerInfo, model: buildLegendModel(layerInfo) }))
      .filter(c => c.model);
  }

  /**
   * 범례에 넣기로 한 레이어 id 집합.
   * 아직 고른 적이 없으면(null) 지도에 보이는 레이어를 기본값으로 삼는다.
   */
  legendSelection(candidates) {
    const picked = this.elements.legend.selectedLayerIds;
    if (picked) return new Set(picked);
    return new Set(candidates.filter(c => c.layerInfo.visible).map(c => c.layerInfo.id));
  }

  /**
   * 현재 선택으로 범례 모델 배열을 만든다. 그리기(내보내기·미리보기)가 공유한다.
   */
  legendModels() {
    const candidates = this.legendCandidates();
    const selected = this.legendSelection(candidates);
    return candidates.filter(c => selected.has(c.layerInfo.id)).map(c => c.model);
  }

  async refreshMapCapture() {
    if (this.capturingPreview) return;
    this.capturingPreview = true;
    this.updatePreview();
    try {
      const canvas = await exportTool.captureMap({ scale: 1.5, includeBasemap: this.includeBasemap });
      this.mapPreviewCanvas = canvas;
      this.mapPreviewBasemap = this.includeBasemap;
    } catch (err) {
      console.warn('Map preview capture failed:', err);
    } finally {
      this.capturingPreview = false;
      this.updatePreview();
    }
  }

  render() {
    this.close();

    const formats = exportTool.getFormats();

    this.modal = document.createElement('div');
    this.modal.className = 'export-modal';
    this.modal.innerHTML = this.getModalHTML(formats);
    document.body.appendChild(this.modal);

    this.addStyles();
    this.syncUIFromElements();
    this.bindEvents();
  }

  getModalHTML(formats) {
    const formatOptions = formats.map(f =>
      '<option value="' + f.value + '">' + f.label + '</option>'
    ).join('');

    return `
      <div class="export-content export-content-large">
        <div class="export-header">
          <h3>지도 내보내기</h3>
          <button class="export-close" id="export-close">&times;</button>
        </div>
        <div class="export-body-grid">
          <!-- 왼쪽: 옵션 -->
          <div class="export-options">
            <div class="export-section">
              <h4>기본 설정</h4>
              <div class="export-form-group">
                <label for="export-filename">파일 이름</label>
                <input type="text" id="export-filename" value="e-GIS_map" placeholder="파일 이름">
              </div>
              <div class="export-form-group">
                <label for="export-format">파일 형식</label>
                <select id="export-format">${formatOptions}</select>
              </div>
              <div class="export-form-group" id="quality-group" style="display:none;">
                <label for="export-quality">이미지 품질</label>
                <input type="range" id="export-quality" min="0.5" max="1" step="0.1" value="0.92">
                <span id="export-quality-value">92%</span>
              </div>
              <div class="export-form-group">
                <label for="export-scale">해상도 배율</label>
                <select id="export-scale">
                  <option value="1">1x (기본)</option>
                  <option value="2" selected>2x (고해상도)</option>
                  <option value="3">3x (초고해상도)</option>
                </select>
              </div>
              <div class="export-form-group basemap-toggle-group">
                <label class="basemap-toggle">
                  <input type="checkbox" id="opt-basemap" checked>
                  <span>웹 배경지도 포함</span>
                </label>
                <p class="basemap-hint">해제하면 OSM/카토 등 타일 배경 없이 업로드한 레이어만 내보냅니다.</p>
              </div>
            </div>

            <div class="export-section">
              <h4>지도 요소 <span class="help-text">(미리보기에서 드래그하여 위치 조정)</span></h4>

              <!-- 제목 -->
              <div class="export-element-group">
                <label class="export-toggle">
                  <input type="checkbox" id="opt-title">
                  <span>제목</span>
                </label>
                <div class="element-options" id="title-options" style="display:none;">
                  <input type="text" id="title-text" placeholder="제목 입력">
                  ${this.getStyleOptionsHTML('title')}
                </div>
              </div>

              <!-- 방위표 -->
              <div class="export-element-group">
                <label class="export-toggle">
                  <input type="checkbox" id="opt-compass" checked>
                  <span>방위표</span>
                </label>
                <div class="element-options" id="compass-options">
                  <div class="style-row">
                    <label>스타일</label>
                    <select id="compass-style">
                      <option value="basic">기본 (N/S 화살표)</option>
                      <option value="arrow">간단 화살표</option>
                      <option value="rose">8방위 별</option>
                      <option value="classic">고전 나침반</option>
                      <option value="custom">커스텀 이미지</option>
                    </select>
                  </div>
                  <div class="style-row" id="compass-image-row" style="display:none;">
                    <label>이미지</label>
                    <div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;min-width:0;">
                      <button type="button" class="btn btn-sm" id="compass-image-btn">이미지 선택</button>
                      <span id="compass-image-name" class="help-text" style="overflow:hidden;text-overflow:ellipsis;max-width:140px;white-space:nowrap;"></span>
                    </div>
                  </div>
                  <div class="style-row">
                    <label>크기</label>
                    <input type="number" id="compass-size" min="30" max="100">
                  </div>
                </div>
              </div>

              <!-- 범례 -->
              <div class="export-element-group">
                <label class="export-toggle">
                  <input type="checkbox" id="opt-legend">
                  <span>범례</span>
                </label>
                <div class="element-options" id="legend-options" style="display:none;">
                  <div class="style-row">
                    <label class="checkbox-inline">
                      <input type="checkbox" id="legend-show-header">
                      <span>제목</span>
                    </label>
                    <input type="text" id="legend-header-text" placeholder="범례" style="flex:1; margin:0;">
                  </div>
                  <p class="help-text" id="legend-layers-hint" style="margin:6px 0 4px;">범례에 넣을 레이어</p>
                  <div class="legend-layers" id="legend-layers"></div>
                  ${this.getBasicStyleHTML('legend')}
                </div>
              </div>

              <!-- 지도 위 범례 / 축척바 -->
              <div class="export-element-group">
                <label class="export-toggle">
                  <span>지도 위 범례 · 축척바</span>
                </label>
                <p class="help-text" style="margin: 0 0 6px; font-weight: normal;">범례와 축척바는 지도 위에서 직접 드래그해 위치를 옮길 수 있습니다.</p>
                <div class="element-options" id="map-elements-options">
                  <div class="style-row">
                    <label class="checkbox-inline">
                      <input type="checkbox" id="opt-map-legend" checked>
                      <span>범례 표시</span>
                    </label>
                  </div>
                  <div class="style-row">
                    <label class="checkbox-inline">
                      <input type="checkbox" id="opt-map-scalebar" checked>
                      <span>축척바 표시</span>
                    </label>
                  </div>
                  <div class="style-row">
                    <label>범례 글자</label>
                    <input type="number" id="map-legend-fontsize" min="8" max="24">
                  </div>
                </div>
              </div>

              <!-- 텍스트 박스 -->
              <div class="export-element-group">
                <label class="export-toggle">
                  <input type="checkbox" id="opt-textbox">
                  <span>텍스트 박스</span>
                </label>
                <div class="element-options" id="textbox-options" style="display:none;">
                  <textarea id="textbox-content" rows="3" placeholder="추가할 텍스트 입력"></textarea>
                  ${this.getStyleOptionsHTML('textbox')}
                </div>
              </div>
            </div>
          </div>

          <!-- 오른쪽: 미리보기 -->
          <div class="export-preview-container">
            <h4>미리보기 <span class="help-text">(요소를 드래그하여 이동)</span></h4>
            <div class="export-preview" id="export-preview">
              <canvas id="preview-canvas"></canvas>
            </div>
            <div class="preview-actions">
              <button class="btn btn-secondary btn-sm" id="preview-expand" title="크게 보기">🔍 크게 보기</button>
              <span style="flex:1;"></span>
              <button class="btn btn-secondary btn-sm" id="reset-positions">위치 초기화</button>
              <button class="btn btn-secondary btn-sm" id="refresh-preview">새로고침</button>
            </div>
          </div>
        </div>
        <div class="export-footer">
          <button class="btn btn-secondary" id="export-cancel">취소</button>
          <button class="btn btn-primary" id="export-apply">내보내기</button>
        </div>
      </div>
    `;
  }

  getStyleOptionsHTML(prefix) {
    const fontOptions = this.fonts.map(f =>
      `<option value="${f.value}">${f.label}</option>`
    ).join('');

    return `
      <div class="style-options">
        <div class="style-row">
          <label>글꼴</label>
          <select id="${prefix}-font">${fontOptions}</select>
        </div>
        <div class="style-row">
          <label>크기</label>
          <input type="number" id="${prefix}-size" min="8" max="72">
        </div>
        <div class="style-row">
          <label>굵기</label>
          <select id="${prefix}-weight">
            <option value="normal">보통</option>
            <option value="bold">굵게</option>
          </select>
        </div>
        <div class="style-row">
          <label>색상</label>
          <input type="color" id="${prefix}-color">
        </div>
        <div class="style-row">
          <label class="checkbox-inline">
            <input type="checkbox" id="${prefix}-background">
            <span>배경</span>
          </label>
          <input type="color" id="${prefix}-background-color" style="width:30px;">
          <input type="range" id="${prefix}-background-opacity" min="0" max="1" step="0.05" style="flex:1;">
          <span id="${prefix}-background-opacity-value" style="font-size:11px; width:30px; text-align:right;"></span>
        </div>
        <div class="style-row">
          <label class="checkbox-inline">
            <input type="checkbox" id="${prefix}-shadow">
            <span>그림자</span>
          </label>
          <input type="color" id="${prefix}-shadow-color" style="width:40px;">
        </div>
        <div class="style-row">
          <label class="checkbox-inline">
            <input type="checkbox" id="${prefix}-stroke">
            <span>테두리</span>
          </label>
          <input type="color" id="${prefix}-stroke-color" style="width:40px;">
          <input type="number" id="${prefix}-stroke-width" min="1" max="5" style="width:50px;">
        </div>
      </div>
    `;
  }

  getBasicStyleHTML(prefix) {
    const fontOptions = this.fonts.map(f =>
      `<option value="${f.value}">${f.label}</option>`
    ).join('');

    return `
      <div class="style-options">
        <div class="style-row">
          <label>글꼴</label>
          <select id="${prefix}-font">${fontOptions}</select>
        </div>
        <div class="style-row">
          <label>크기</label>
          <input type="number" id="${prefix}-size" min="8" max="24">
        </div>
        <div class="style-row">
          <label>색상</label>
          <input type="color" id="${prefix}-color">
        </div>
        <div class="style-row">
          <label class="checkbox-inline">
            <input type="checkbox" id="${prefix}-background">
            <span>배경</span>
          </label>
          <input type="color" id="${prefix}-background-color" style="width:30px;">
          <input type="range" id="${prefix}-background-opacity" min="0" max="1" step="0.05" style="flex:1;">
          <span id="${prefix}-background-opacity-value" style="font-size:11px; width:30px; text-align:right;"></span>
        </div>
      </div>
    `;
  }

  /**
   * UI 요소들을 this.elements 값으로 동기화
   */
  syncUIFromElements() {
    // 제목
    const titleEl = this.elements.title;
    this.setChecked('opt-title', titleEl.enabled);
    this.setValue('title-text', titleEl.text);
    this.setValue('title-font', titleEl.fontFamily);
    this.setValue('title-size', titleEl.fontSize);
    this.setValue('title-weight', titleEl.fontWeight);
    this.setValue('title-color', titleEl.color);
    this.setChecked('title-background', titleEl.background);
    this.setValue('title-background-color', titleEl.backgroundColor);
    this.setValue('title-background-opacity', titleEl.backgroundOpacity);
    this.updateOpacityLabel('title', titleEl.backgroundOpacity);
    this.toggleBgControls('title', titleEl.background);
    this.setChecked('title-shadow', titleEl.shadow);
    this.setValue('title-shadow-color', titleEl.shadowColor);
    this.setChecked('title-stroke', titleEl.stroke);
    this.setValue('title-stroke-color', titleEl.strokeColor);
    this.setValue('title-stroke-width', titleEl.strokeWidth);
    document.getElementById('title-options').style.display = titleEl.enabled ? 'block' : 'none';

    // 방위표
    const compassEl = this.elements.compass;
    this.setChecked('opt-compass', compassEl.enabled);
    this.setValue('compass-size', compassEl.size);
    this.setValue('compass-style', compassEl.style || 'basic');

    // 범례
    const legendEl = this.elements.legend;
    this.setChecked('opt-legend', legendEl.enabled);
    this.setChecked('legend-show-header', legendEl.showHeader);
    this.setValue('legend-header-text', legendEl.headerText);
    this.setValue('legend-font', legendEl.fontFamily);
    this.setValue('legend-size', legendEl.fontSize);
    this.setValue('legend-color', legendEl.color);
    this.setChecked('legend-background', legendEl.background);
    this.setValue('legend-background-color', legendEl.backgroundColor);
    this.setValue('legend-background-opacity', legendEl.backgroundOpacity);
    this.updateOpacityLabel('legend', legendEl.backgroundOpacity);
    this.toggleBgControls('legend', legendEl.background);
    document.getElementById('legend-options').style.display = legendEl.enabled ? 'block' : 'none';
    this.renderLegendLayers();

    // 지도 위 요소
    this.setChecked('opt-map-legend', this.mapElements.showLegend);
    this.setChecked('opt-map-scalebar', this.mapElements.showScaleBar);
    this.setValue('map-legend-fontsize', this.mapElements.legendFontSize);

    // 텍스트 박스
    const textBoxEl = this.elements.textBox;
    this.setChecked('opt-textbox', textBoxEl.enabled);
    this.setValue('textbox-content', textBoxEl.text);
    this.setValue('textbox-font', textBoxEl.fontFamily);
    this.setValue('textbox-size', textBoxEl.fontSize);
    this.setValue('textbox-weight', textBoxEl.fontWeight);
    this.setValue('textbox-color', textBoxEl.color);
    this.setChecked('textbox-background', textBoxEl.background);
    this.setValue('textbox-background-color', textBoxEl.backgroundColor);
    this.setValue('textbox-background-opacity', textBoxEl.backgroundOpacity);
    this.updateOpacityLabel('textbox', textBoxEl.backgroundOpacity);
    this.toggleBgControls('textbox', textBoxEl.background);
    this.setChecked('textbox-shadow', textBoxEl.shadow);
    this.setChecked('textbox-stroke', textBoxEl.stroke);
    this.setValue('textbox-stroke-color', textBoxEl.strokeColor);
    this.setValue('textbox-stroke-width', textBoxEl.strokeWidth);
    document.getElementById('textbox-options').style.display = textBoxEl.enabled ? 'block' : 'none';
  }

  /**
   * 범례 레이어 체크박스 목록을 그린다.
   * 넣을 수 있는 레이어가 없거나 일부가 빠졌으면 그 사실을 안내한다.
   */
  renderLegendLayers() {
    const listEl = document.getElementById('legend-layers');
    const hintEl = document.getElementById('legend-layers-hint');
    if (!listEl) return;

    const candidates = this.legendCandidates();
    const excluded = layerManager.getAllLayers().length - candidates.length;

    if (candidates.length === 0) {
      listEl.innerHTML = '<p class="no-layers">범례에 넣을 레이어가 없습니다.</p>';
      if (hintEl) hintEl.textContent = '범례에 넣을 레이어';
      return;
    }

    const selected = this.legendSelection(candidates);
    listEl.innerHTML = candidates.map(({ layerInfo, model }) => {
      const checked = selected.has(layerInfo.id) ? 'checked' : '';
      const swatch = model.grouped
        ? (model.items[0].symbol.fillColor || layerInfo.color)
        : (layerInfo.fillColor || layerInfo.color);
      const suffix = model.grouped ? ` <span class="help-text">(${model.items.length}구간)</span>` : '';
      return `
        <label class="legend-layer-item">
          <input type="checkbox" data-legend-layer="${layerInfo.id}" ${checked}>
          <span class="layer-color" style="background:${swatch}"></span>
          <span>${this.escapeHtml(layerInfo.name)}</span>${suffix}
        </label>`;
    }).join('');

    if (hintEl) {
      hintEl.textContent = excluded > 0
        ? `범례에 넣을 레이어 (래스터·히트맵·도형표현도 ${excluded}개는 지도 위 자체 범례를 씁니다)`
        : '범례에 넣을 레이어';
    }

    listEl.querySelectorAll('[data-legend-layer]').forEach(cb => {
      cb.addEventListener('change', () => this.onLegendLayerToggled());
    });
  }

  /**
   * 체크박스 상태를 selectedLayerIds에 굳힌다.
   * 한 번이라도 건드리면 "보이는 레이어 자동" 기본값에서 벗어나 사용자 선택이 유지된다.
   */
  onLegendLayerToggled() {
    const listEl = document.getElementById('legend-layers');
    if (!listEl) return;
    this.elements.legend.selectedLayerIds = Array.from(
      listEl.querySelectorAll('[data-legend-layer]:checked')
    ).map(cb => cb.dataset.legendLayer);
    this.updatePreview();
  }

  escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  setValue(id, value) {
    const el = document.getElementById(id);
    if (el) el.value = value;
  }

  setChecked(id, checked) {
    const el = document.getElementById(id);
    if (el) el.checked = checked;
  }

  toggleBgControls(prefix, enabled) {
    const color = document.getElementById(`${prefix}-background-color`);
    const opacity = document.getElementById(`${prefix}-background-opacity`);
    const label = document.getElementById(`${prefix}-background-opacity-value`);
    [color, opacity].forEach(el => {
      if (!el) return;
      el.disabled = !enabled;
      el.style.opacity = enabled ? '1' : '0.4';
    });
    if (label) label.style.opacity = enabled ? '1' : '0.4';
  }

  updateOpacityLabel(prefix, value) {
    const label = document.getElementById(`${prefix}-background-opacity-value`);
    if (label) label.textContent = Math.round(value * 100) + '%';
  }

  hexToRgba(hex, alpha) {
    const a = (alpha == null || isNaN(alpha)) ? 1 : alpha;
    if (!hex || hex[0] !== '#') return `rgba(255,255,255,${a})`;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${a})`;
  }

  bindEvents() {
    const closeBtn = document.getElementById('export-close');
    const cancelBtn = document.getElementById('export-cancel');
    const applyBtn = document.getElementById('export-apply');
    const formatSelect = document.getElementById('export-format');
    const qualityInput = document.getElementById('export-quality');
    const qualityValue = document.getElementById('export-quality-value');
    const qualityGroup = document.getElementById('quality-group');
    const refreshBtn = document.getElementById('refresh-preview');
    const resetBtn = document.getElementById('reset-positions');

    closeBtn.addEventListener('click', () => this.close());
    cancelBtn.addEventListener('click', () => this.close());

    formatSelect.addEventListener('change', (e) => {
      qualityGroup.style.display = e.target.value === 'jpg' ? 'block' : 'none';
    });

    qualityInput.addEventListener('input', (e) => {
      qualityValue.textContent = Math.round(e.target.value * 100) + '%';
    });

    // 배경지도 토글
    const basemapToggle = document.getElementById('opt-basemap');
    if (basemapToggle) {
      basemapToggle.checked = this.includeBasemap;
      basemapToggle.addEventListener('change', (e) => {
        this.includeBasemap = e.target.checked;
        this.refreshMapCapture();
      });
    }

    // 제목 토글
    document.getElementById('opt-title').addEventListener('change', (e) => {
      this.elements.title.enabled = e.target.checked;
      document.getElementById('title-options').style.display = e.target.checked ? 'block' : 'none';
      this.updatePreview();
    });

    // 방위표 토글
    document.getElementById('opt-compass').addEventListener('change', (e) => {
      this.elements.compass.enabled = e.target.checked;
      this.updatePreview();
    });

    // 범례 토글
    document.getElementById('opt-legend').addEventListener('change', (e) => {
      this.elements.legend.enabled = e.target.checked;
      document.getElementById('legend-options').style.display = e.target.checked ? 'block' : 'none';
      // 열 때마다 목록을 다시 그린다 — 패널을 띄운 뒤 레이어가 바뀌었을 수 있다.
      if (e.target.checked) this.renderLegendLayers();
      this.updatePreview();
    });

    // 범례 제목·스타일
    this.bindCheckbox('legend-show-header', 'legend', 'showHeader');
    this.bindInput('legend-header-text', 'legend', 'headerText');
    this.bindInput('legend-font', 'legend', 'fontFamily');
    this.bindInput('legend-size', 'legend', 'fontSize', true);
    this.bindInput('legend-color', 'legend', 'color');
    this.bindBackground('legend');

    // 지도 위 범례·축척바
    const mapLegendCb = document.getElementById('opt-map-legend');
    if (mapLegendCb) {
      mapLegendCb.addEventListener('change', (e) => {
        this.mapElements.showLegend = e.target.checked;
        this.applyMapElements();
        this.refreshMapCapture();
      });
    }
    const mapScaleCb = document.getElementById('opt-map-scalebar');
    if (mapScaleCb) {
      mapScaleCb.addEventListener('change', (e) => {
        this.mapElements.showScaleBar = e.target.checked;
        this.applyMapElements();
        this.refreshMapCapture();
      });
    }
    const mapLegendFs = document.getElementById('map-legend-fontsize');
    if (mapLegendFs) {
      mapLegendFs.addEventListener('change', (e) => {
        this.mapElements.legendFontSize = parseInt(e.target.value) || 12;
        this.applyMapElements();
        this.refreshMapCapture();
      });
    }

    // 텍스트 박스 토글
    document.getElementById('opt-textbox').addEventListener('change', (e) => {
      this.elements.textBox.enabled = e.target.checked;
      document.getElementById('textbox-options').style.display = e.target.checked ? 'block' : 'none';
      this.updatePreview();
    });

    // 제목 스타일
    this.bindInput('title-text', 'title', 'text');
    this.bindInput('title-font', 'title', 'fontFamily');
    this.bindInput('title-size', 'title', 'fontSize', true);
    this.bindInput('title-weight', 'title', 'fontWeight');
    this.bindInput('title-color', 'title', 'color');
    this.bindBackground('title');
    this.bindCheckbox('title-shadow', 'title', 'shadow');
    this.bindInput('title-shadow-color', 'title', 'shadowColor');
    this.bindCheckbox('title-stroke', 'title', 'stroke');
    this.bindInput('title-stroke-color', 'title', 'strokeColor');
    this.bindInput('title-stroke-width', 'title', 'strokeWidth', true);

    // 방위표 크기·스타일
    this.bindInput('compass-size', 'compass', 'size', true);
    this.bindInput('compass-style', 'compass', 'style');

    // 커스텀 이미지 방위표: 스타일이 '커스텀'일 때 이미지 업로드 행 표시
    const compassStyleSel = document.getElementById('compass-style');
    if (compassStyleSel) {
      compassStyleSel.addEventListener('change', () => this.updateCompassImageRow());
    }
    this.setupCompassImageUpload();
    this.updateCompassImageRow();

    // 텍스트 박스 스타일
    this.bindInput('textbox-content', 'textBox', 'text');
    this.bindInput('textbox-font', 'textBox', 'fontFamily');
    this.bindInput('textbox-size', 'textBox', 'fontSize', true);
    this.bindInput('textbox-weight', 'textBox', 'fontWeight');
    this.bindInput('textbox-color', 'textBox', 'color');
    this.bindBackground('textbox', 'textBox');
    this.bindCheckbox('textbox-shadow', 'textBox', 'shadow');
    this.bindCheckbox('textbox-stroke', 'textBox', 'stroke');
    this.bindInput('textbox-stroke-color', 'textBox', 'strokeColor');
    this.bindInput('textbox-stroke-width', 'textBox', 'strokeWidth', true);

    refreshBtn.addEventListener('click', () => this.refreshMapCapture());

    const expandBtn = document.getElementById('preview-expand');
    if (expandBtn) expandBtn.addEventListener('click', () => this.openZoomedPreview());
    resetBtn.addEventListener('click', () => this.resetPositions());
    applyBtn.addEventListener('click', () => this.doExport());

    // 캔버스 드래그 이벤트
    this.setupDragEvents();

    // 초기 미리보기
    setTimeout(() => this.updatePreview(), 100);
  }

  bindInput(elementId, objKey, propKey, isNumber = false) {
    const el = document.getElementById(elementId);
    if (!el) return;

    const handler = (e) => {
      this.elements[objKey][propKey] = isNumber ? parseInt(e.target.value) || 0 : e.target.value;
      this.updatePreview();
    };

    el.addEventListener('input', handler);
    el.addEventListener('change', handler);
  }

  bindBackground(prefix, objKey = prefix) {
    const checkbox = document.getElementById(`${prefix}-background`);
    const color = document.getElementById(`${prefix}-background-color`);
    const opacity = document.getElementById(`${prefix}-background-opacity`);

    if (checkbox) {
      checkbox.addEventListener('change', (e) => {
        this.elements[objKey].background = e.target.checked;
        this.toggleBgControls(prefix, e.target.checked);
        this.updatePreview();
      });
    }
    if (color) {
      color.addEventListener('input', (e) => {
        this.elements[objKey].backgroundColor = e.target.value;
        this.updatePreview();
      });
    }
    if (opacity) {
      opacity.addEventListener('input', (e) => {
        const v = parseFloat(e.target.value);
        this.elements[objKey].backgroundOpacity = v;
        this.updateOpacityLabel(prefix, v);
        this.updatePreview();
      });
    }
  }

  bindCheckbox(elementId, objKey, propKey) {
    const el = document.getElementById(elementId);
    if (!el) return;

    el.addEventListener('change', (e) => {
      this.elements[objKey][propKey] = e.target.checked;
      this.updatePreview();
    });
  }

  setupDragEvents() {
    const canvas = document.getElementById('preview-canvas');
    if (!canvas) return;
    this.attachCanvasDrag(canvas, () => this.updatePreview());
  }

  /**
   * 캔버스에 드래그 핸들러 부착 (작은 미리보기/줌 모달 공용)
   * canvas.width(내부 버퍼) ↔ rect.width(표시) 비율로 좌표 변환하므로
   * DPR 스케일이나 줌 배율에 관계없이 동일하게 동작
   */
  attachCanvasDrag(canvas, redraw) {
    const toBufferCoords = (e) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY
      };
    };

    // 오버레이 위치 기준이 되는 지도 사각형(버퍼 픽셀). 없으면 캔버스 전체로 폴백.
    const getMapRect = () => canvas._mapRect || { x: 0, y: 0, w: canvas.width, h: canvas.height };

    canvas.addEventListener('mousedown', (e) => {
      const { x, y } = toBufferCoords(e);
      const mr = getMapRect();
      const hit = this.hitTestAt(x, y, mr);
      if (hit) {
        this.dragging = hit;
        this.dragOffset = {
          x: x - (mr.x + this.elements[hit].x * mr.w),
          y: y - (mr.y + this.elements[hit].y * mr.h)
        };
        canvas.style.cursor = 'grabbing';
      }
    });

    canvas.addEventListener('mousemove', (e) => {
      const { x, y } = toBufferCoords(e);
      const mr = getMapRect();
      if (this.dragging) {
        this.elements[this.dragging].x = Math.max(0.05, Math.min(0.95, (x - this.dragOffset.x - mr.x) / mr.w));
        this.elements[this.dragging].y = Math.max(0.05, Math.min(0.95, (y - this.dragOffset.y - mr.y) / mr.h));
        redraw();
      } else {
        const hit = this.hitTestAt(x, y, mr);
        canvas.style.cursor = hit ? 'grab' : 'default';
      }
    });

    const endDrag = () => {
      canvas.style.cursor = 'default';
      this.dragging = null;
    };
    canvas.addEventListener('mouseup', endDrag);
    canvas.addEventListener('mouseleave', endDrag);
  }

  hitTest(x, y) {
    const canvas = document.getElementById('preview-canvas');
    const mr = canvas._mapRect || { x: 0, y: 0, w: canvas.width, h: canvas.height };
    return this.hitTestAt(x, y, mr);
  }

  hitTestAt(x, y, mr) {
    // 뒤에 그려진 요소가 위에 오므로, 겹칠 때 위 요소가 먼저 잡히도록 역순으로 본다.
    const elements = ['textBox', 'legend', 'compass', 'title'];

    for (const key of elements) {
      const el = this.elements[key];
      if (!el.enabled) continue;

      const elX = mr.x + el.x * mr.w;
      const elY = mr.y + el.y * mr.h;
      let hitBox = { x: 0, y: 0, w: 0, h: 0 };

      switch (key) {
        case 'title':
          hitBox = { x: elX - 100, y: elY - 20, w: 200, h: 40 };
          break;
        case 'compass':
          const size = el.size * (this._lastPreviewFontScale || 0.4);
          hitBox = { x: elX - size, y: elY - size, w: size * 2, h: size * 2 };
          break;
        case 'legend': {
          // 크기·위치 모두 그릴 때 정해진다(내용에 따라 커지고, 지도 밖이면 당겨 붙는다).
          // 설정값이 아니라 마지막으로 그린 박스를 그대로 쓴다.
          if (!this._legendBox) continue;
          const b = this._legendBox;
          hitBox = {
            x: mr.x + b.xFrac * mr.w,
            y: mr.y + b.yFrac * mr.h,
            w: b.wFrac * mr.w,
            h: b.hFrac * mr.h
          };
          break;
        }
        case 'textBox':
          hitBox = { x: elX - 50, y: elY - 25, w: 100, h: 50 };
          break;
      }

      if (x >= hitBox.x && x <= hitBox.x + hitBox.w &&
          y >= hitBox.y && y <= hitBox.y + hitBox.h) {
        return key;
      }
    }

    return null;
  }

  resetPositions() {
    this.elements.title.x = 0.5;
    this.elements.title.y = 0.08;
    this.elements.compass.x = 0.92;
    this.elements.compass.y = 0.12;
    this.elements.legend.x = 0.78;
    this.elements.legend.y = 0.5;
    this.elements.textBox.x = 0.5;
    this.elements.textBox.y = 0.85;
    this.updatePreview();
  }

  openZoomedPreview() {
    // 기존 큰 미리보기 모달이 있다면 제거
    document.getElementById('export-zoom-modal')?.remove();

    const modal = document.createElement('div');
    modal.id = 'export-zoom-modal';
    modal.className = 'export-zoom-modal';
    modal.innerHTML = `
      <div class="export-zoom-content">
        <div class="export-zoom-header">
          <span>미리보기 크게 보기</span>
          <div class="export-zoom-controls">
            <button class="btn btn-secondary btn-sm" data-zoom="out">−</button>
            <span id="zoom-modal-value">100%</span>
            <button class="btn btn-secondary btn-sm" data-zoom="in">+</button>
            <button class="btn btn-secondary btn-sm" data-zoom="reset">↺</button>
            <button class="btn btn-secondary btn-sm" id="zoom-modal-close">닫기</button>
          </div>
        </div>
        <div class="export-zoom-body" id="zoom-modal-body">
          <canvas id="zoom-preview-canvas"></canvas>
        </div>
      </div>
    `;
    document.body.appendChild(modal);

    let zoom = 1;
    const canvas = modal.querySelector('#zoom-preview-canvas');
    const body = modal.querySelector('#zoom-modal-body');
    const valueLabel = modal.querySelector('#zoom-modal-value');

    const render = () => {
      const bodyW = body.clientWidth - 16;
      const baseW = Math.max(900, bodyW);
      const baseH = Math.round(baseW * 0.67);
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      canvas.width = baseW * dpr;
      canvas.height = baseH * dpr;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      const mr = this._renderPreviewInto(ctx, baseW, baseH);
      // 드래그 좌표 변환용 지도 사각형(버퍼 픽셀) 저장
      canvas._mapRect = { x: mr.x * dpr, y: mr.y * dpr, w: mr.w * dpr, h: mr.h * dpr };
      canvas.style.width = (baseW * zoom) + 'px';
      canvas.style.height = 'auto';
      if (valueLabel) valueLabel.textContent = Math.round(zoom * 100) + '%';
    };

    modal.querySelectorAll('[data-zoom]').forEach(btn => {
      btn.addEventListener('click', () => {
        const dir = btn.dataset.zoom;
        if (dir === 'in') zoom = Math.min(4, zoom * 1.25);
        else if (dir === 'out') zoom = Math.max(0.5, zoom / 1.25);
        else zoom = 1;
        render();
      });
    });
    const closeZoom = () => {
      modal.remove();
      // 줌 모달에서 옮긴 위치를 작은 미리보기에 반영
      this.updatePreview();
    };
    modal.querySelector('#zoom-modal-close').addEventListener('click', closeZoom);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeZoom();
    });

    setTimeout(() => {
      render();
      // 줌 캔버스에도 드래그 부착 — 작은 미리보기와 동일한 좌표/엘리먼트 사용
      this.attachCanvasDrag(canvas, render);
    }, 10);
  }

  /**
   * 별도 캔버스에 미리보기 렌더 (지도 + 오버레이 요소)
   */
  _renderPreviewInto(ctx, w, h) {
    ctx.fillStyle = '#e8e8e8';
    ctx.fillRect(0, 0, w, h);
    const mapX = 8, mapY = 8, mapW = w - 16, mapH = h - 16;
    let mapRect = { x: mapX, y: mapY, w: mapW, h: mapH };

    const hasCachedMap = this.mapPreviewCanvas && this.mapPreviewBasemap === this.includeBasemap;
    if (!this.includeBasemap) {
      const tile = 12;
      for (let y = mapY; y < mapY + mapH; y += tile) {
        for (let x = mapX; x < mapX + mapW; x += tile) {
          ctx.fillStyle = ((x + y) / tile) % 2 === 0 ? '#ffffff' : '#f0f0f0';
          ctx.fillRect(x, y, Math.min(tile, mapX + mapW - x), Math.min(tile, mapY + mapH - y));
        }
      }
    } else {
      ctx.fillStyle = '#c5d8e8';
      ctx.fillRect(mapX, mapY, mapW, mapH);
    }
    if (hasCachedMap) {
      const src = this.mapPreviewCanvas;
      const srcRatio = src.width / src.height;
      const dstRatio = mapW / mapH;
      let dw, dh, dx, dy;
      if (srcRatio > dstRatio) {
        dw = mapW; dh = mapW / srcRatio;
        dx = mapX; dy = mapY + (mapH - dh) / 2;
      } else {
        dh = mapH; dw = mapH * srcRatio;
        dy = mapY; dx = mapX + (mapW - dw) / 2;
      }
      ctx.drawImage(src, dx, dy, dw, dh);
      mapRect = { x: dx, y: dy, w: dw, h: dh };
    }
    this.drawPreviewElements(ctx, mapRect, this._previewFontScale(mapRect.w));
    return mapRect;
  }

  updatePreview() {
    const container = document.getElementById('export-preview');
    const canvas = document.getElementById('preview-canvas');
    if (!container || !canvas) return;

    const cssWidth = container.clientWidth - 10 || 300;
    const cssHeight = Math.round(cssWidth * 0.67);
    const dpr = Math.min(window.devicePixelRatio || 1, 3);

    // 실제 픽셀버퍼는 dpr 배수로
    canvas.width = cssWidth * dpr;
    canvas.height = cssHeight * dpr;
    canvas.style.width = cssWidth + 'px';
    canvas.style.height = cssHeight + 'px';

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    // 이하 코드에서 사용할 “논리적” 크기로 변수 덮어쓰기
    const previewWidth = cssWidth;
    const previewHeight = cssHeight;

    // 배경
    ctx.fillStyle = '#e8e8e8';
    ctx.fillRect(0, 0, previewWidth, previewHeight);

    const mapX = 8;
    const mapY = 8;
    const mapW = previewWidth - 16;
    const mapH = previewHeight - 16;

    // 지도 영역 - 캡처된 지도 또는 fallback
    const hasCachedMap = this.mapPreviewCanvas && this.mapPreviewBasemap === this.includeBasemap;

    if (!this.includeBasemap) {
      // 투명 배경 표현 (체크무늬)
      const tile = 8;
      for (let y = mapY; y < mapY + mapH; y += tile) {
        for (let x = mapX; x < mapX + mapW; x += tile) {
          const isAlt = ((x + y) / tile) % 2 === 0;
          ctx.fillStyle = isAlt ? '#ffffff' : '#f0f0f0';
          ctx.fillRect(
            x,
            y,
            Math.min(tile, mapX + mapW - x),
            Math.min(tile, mapY + mapH - y)
          );
        }
      }
    } else {
      ctx.fillStyle = '#c5d8e8';
      ctx.fillRect(mapX, mapY, mapW, mapH);
    }

    // 지도가 실제로 표시된 사각형 (오버레이 위치·크기 기준)
    let mapRect = { x: mapX, y: mapY, w: mapW, h: mapH };
    if (hasCachedMap) {
      // 캡처된 지도 그리기 (aspect-fit)
      const src = this.mapPreviewCanvas;
      const srcRatio = src.width / src.height;
      const dstRatio = mapW / mapH;
      let dw, dh, dx, dy;
      if (srcRatio > dstRatio) {
        dw = mapW; dh = mapW / srcRatio;
        dx = mapX; dy = mapY + (mapH - dh) / 2;
      } else {
        dh = mapH; dw = mapH * srcRatio;
        dy = mapY; dx = mapX + (mapW - dw) / 2;
      }
      ctx.drawImage(src, dx, dy, dw, dh);
      mapRect = { x: dx, y: dy, w: dw, h: dh };
    } else if (this.capturingPreview) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.font = '12px "Malgun Gothic", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('지도 캡처 중...', mapX + mapW / 2, mapY + mapH / 2);
    }

    // 요소 그리기 (내보내기와 동일한 지도 기준 위치·배율로)
    this.drawPreviewElements(ctx, mapRect, this._previewFontScale(mapRect.w));

    // 드래그 좌표 변환용 지도 사각형(버퍼 픽셀) 저장
    canvas._mapRect = { x: mapRect.x * dpr, y: mapRect.y * dpr, w: mapRect.w * dpr, h: mapRect.h * dpr };
  }

  drawPreviewElements(ctx, mapRect, fontScale = 0.5) {
    // 오버레이를 "표시된 지도 사각형" 기준으로 그린다 → 내보내기(지도=캔버스 전체)와 위치·크기 일치
    ctx.save();
    ctx.translate(mapRect.x, mapRect.y);
    const w = mapRect.w;
    const h = mapRect.h;
    if (this.elements.title.enabled) {
      this.drawPreviewTitle(ctx, w, h, fontScale);
    }
    if (this.elements.compass.enabled) {
      this.drawPreviewCompass(ctx, w, h, fontScale);
    }
    if (this.elements.legend.enabled) {
      this.drawPreviewLegend(ctx, w, h, fontScale);
    }
    if (this.elements.textBox.enabled && this.elements.textBox.text) {
      this.drawPreviewTextBox(ctx, w, h, fontScale);
    }
    ctx.restore();
  }

  /**
   * 범례 미리보기 — 내보내기와 같은 함수로 그려 결과물과 어긋나지 않게 한다.
   *
   * 박스 크기는 지도 사각형 대비 "비율"로 캐시한다. 히트 테스트는 캔버스 버퍼
   * 픽셀로 들어오는데 여기 width/height는 논리 픽셀이라, 픽셀 값을 그대로 두면
   * DPR이나 줌 배율만큼 어긋난다. 비율은 어느 좌표계에서도 곱하기만 하면 된다.
   */
  drawPreviewLegend(ctx, width, height, fontScale) {
    const box = exportTool.drawLegend(
      ctx,
      { ...this.elements.legend, models: this.legendModels() },
      width, height, fontScale
    );
    // 위치까지 담는다 — drawLegend가 지도 밖으로 나가는 박스를 당겨 붙이므로,
    // 설정값(x, y)이 아니라 "실제로 그린 자리"를 잡아야 드래그가 어긋나지 않는다.
    this._legendBox = box
      ? { xFrac: box.x / width, yFrac: box.y / height, wFrac: box.w / width, hFrac: box.h / height }
      : null;

    // 당겨 붙은 위치를 설정값에 되쓴다. 안 그러면 가장자리에서 설정값만 계속 밀려
    // 드래그로 되돌릴 때 그만큼 헛돈다.
    if (this._legendBox) {
      this.elements.legend.x = this._legendBox.xFrac;
      this.elements.legend.y = this._legendBox.yFrac;
    }
  }

  /**
   * 미리보기 오버레이 폰트/크기 배율.
   * 표시된 지도 폭(displayedMapW) ÷ 실제 지도 폭(#map clientWidth)으로 계산해,
   * 내보내기(fontSize × exportScale, 지도폭 = clientWidth × exportScale)와
   * "지도 대비 상대 크기"를 동일하게 맞춘다 → 미리보기가 곧 결과물(WYSIWYG).
   */
  _previewFontScale(displayedMapW) {
    const mapEl = document.getElementById('map');
    const clientW = (mapEl && mapEl.clientWidth) || displayedMapW || 1;
    this._lastPreviewFontScale = displayedMapW / clientW;
    return this._lastPreviewFontScale;
  }

  drawPreviewTitle(ctx, width, height, fontScale = 0.5) {
    const el = this.elements.title;
    const x = el.x * width;
    const y = el.y * height;
    const fontSize = Math.max(6, el.fontSize * fontScale);

    ctx.save();

    ctx.font = `${el.fontWeight} ${fontSize}px "${el.fontFamily}", sans-serif`;
    const textWidth = ctx.measureText(el.text || '지도 제목').width + 20;

    // 배경
    if (el.background) {
      ctx.fillStyle = this.hexToRgba(el.backgroundColor, el.backgroundOpacity);
      ctx.fillRect(x - textWidth / 2, y - fontSize, textWidth, fontSize * 1.8);
    }

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (el.shadow) {
      ctx.shadowColor = el.shadowColor;
      ctx.shadowBlur = 4;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 2;
    }

    if (el.stroke) {
      ctx.strokeStyle = el.strokeColor;
      ctx.lineWidth = el.strokeWidth * fontScale;
      ctx.strokeText(el.text || '지도 제목', x, y);
    }

    ctx.fillStyle = el.color;
    ctx.fillText(el.text || '지도 제목', x, y);

    ctx.restore();
  }

  drawPreviewScaleBar_unused(ctx, width, height) {
    const el = this.elements.scaleBar;
    const x = el.x * width;
    const y = el.y * height;
    const fontSize = Math.max(6, el.fontSize * 0.5);

    ctx.save();

    if (el.background) {
      ctx.fillStyle = this.hexToRgba(el.backgroundColor, el.backgroundOpacity);
      ctx.fillRect(x - 5, y - 20, 70, 30);
    }

    ctx.fillStyle = '#333';
    ctx.fillRect(x, y, 50, 4);
    ctx.fillStyle = '#fff';
    ctx.fillRect(x, y, 25, 4);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 0.5;
    ctx.strokeRect(x, y, 50, 4);

    ctx.font = `${fontSize}px "${el.fontFamily}", sans-serif`;
    ctx.fillStyle = el.color;
    ctx.textAlign = 'center';
    ctx.fillText('0', x, y - 5);
    ctx.fillText('1km', x + 50, y - 5);

    ctx.restore();
  }

  drawPreviewCompass(ctx, width, height, fontScale = 0.4) {
    // ExportTool과 동일한 그리기 로직을 지도 대비 동일 배율(fontScale)로 렌더
    exportTool.drawCompass(ctx, this.elements.compass, width, height, fontScale);
  }

  /**
   * 커스텀 이미지 방위표 - 업로드 행 표시/숨김 및 파일명 갱신
   */
  updateCompassImageRow() {
    const row = document.getElementById('compass-image-row');
    if (row) {
      row.style.display = this.elements.compass.style === 'custom' ? '' : 'none';
    }
    const nameEl = document.getElementById('compass-image-name');
    if (nameEl) {
      nameEl.textContent = this.elements.compass.imageName ||
        (this.elements.compass.imageSrc ? '이미지 설정됨' : '이미지를 선택하세요');
    }
  }

  /**
   * 커스텀 이미지 업로드 버튼/입력 설정
   */
  setupCompassImageUpload() {
    if (!this.compassImageInput) {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.style.display = 'none';
      document.body.appendChild(input);
      input.addEventListener('change', (e) => this.onCompassImageSelected(e));
      this.compassImageInput = input;
    }
    const btn = document.getElementById('compass-image-btn');
    if (btn) {
      btn.addEventListener('click', () => this.compassImageInput.click());
    }
  }

  /**
   * 이미지 선택 처리 - data URL로 읽어 Image 객체로 로드 후 미리보기 갱신
   */
  onCompassImageSelected(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 사용할 수 있습니다.');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const img = new Image();
      img.onload = () => {
        this.elements.compass.imageSrc = dataUrl;
        this.elements.compass.image = img;
        this.elements.compass.imageName = file.name;
        // 커스텀 이미지를 올리면 스타일도 자동으로 '커스텀'으로
        this.elements.compass.style = 'custom';
        this.setValue('compass-style', 'custom');
        this.updateCompassImageRow();
        this.updatePreview();
      };
      img.onerror = () => alert('이미지를 불러오지 못했습니다.');
      img.src = dataUrl;
    };
    reader.onerror = () => alert('파일을 읽지 못했습니다.');
    reader.readAsDataURL(file);
  }

  drawPreviewTextBox(ctx, width, height, fontScale = 0.5) {
    const el = this.elements.textBox;
    const x = el.x * width;
    const y = el.y * height;
    const fontSize = Math.max(6, el.fontSize * fontScale);
    const lines = el.text.split('\n').slice(0, 3).map(l => l.substring(0, 20));

    ctx.save();

    ctx.font = `${el.fontWeight} ${fontSize}px "${el.fontFamily}", sans-serif`;
    const lineHeight = fontSize + 4;
    const maxLineWidth = Math.max(...lines.map(l => ctx.measureText(l).width));
    const boxWidth = maxLineWidth + 16;
    const boxHeight = lines.length * lineHeight + 8;
    const boxX = x - boxWidth / 2;
    const boxY = y - boxHeight / 2;

    if (el.background) {
      ctx.fillStyle = this.hexToRgba(el.backgroundColor, el.backgroundOpacity);
      ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
    }

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (el.shadow) {
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 2;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;
    }

    const firstLineY = y - (lines.length - 1) * lineHeight / 2;

    lines.forEach((line, i) => {
      const lineY = firstLineY + i * lineHeight;

      if (el.stroke) {
        ctx.strokeStyle = el.strokeColor;
        ctx.lineWidth = el.strokeWidth * 0.3;
        ctx.strokeText(line, x, lineY);
      }

      ctx.fillStyle = el.color;
      ctx.fillText(line, x, lineY);
    });

    ctx.restore();
  }

  async doExport() {
    const filename = document.getElementById('export-filename').value.trim() || 'e-GIS_map';
    const format = document.getElementById('export-format').value;
    const quality = parseFloat(document.getElementById('export-quality').value);
    const scale = parseInt(document.getElementById('export-scale').value);

    const legendModels = this.elements.legend.enabled ? this.legendModels() : [];
    const overlayOptions = {
      title: this.elements.title.enabled ? { ...this.elements.title } : null,
      compass: this.elements.compass.enabled ? { ...this.elements.compass } : null,
      legend: legendModels.length > 0
        ? { ...this.elements.legend, models: legendModels }
        : null,
      textBox: (this.elements.textBox.enabled && this.elements.textBox.text)
        ? { ...this.elements.textBox }
        : null
    };

    const applyBtn = document.getElementById('export-apply');
    applyBtn.disabled = true;
    applyBtn.textContent = '내보내는 중...';

    try {
      await exportTool.exportMapWithOverlays({
        filename,
        format,
        quality,
        scale,
        overlays: overlayOptions,
        includeBasemap: this.includeBasemap
      });

      this.close();
    } catch (error) {
      console.error('Export error:', error);
      alert('내보내기 실패: ' + error.message);
      applyBtn.disabled = false;
      applyBtn.textContent = '내보내기';
    }
  }


  addStyles() {
    if (document.getElementById('export-panel-styles-v4')) return;

    const styles = document.createElement('style');
    styles.id = 'export-panel-styles-v4';
    styles.textContent = `
      .export-modal {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
      }

      .export-content {
        background: var(--bg-panel, #fff);
        border-radius: var(--radius-lg, 8px);
        box-shadow: var(--shadow-lg, 0 4px 20px rgba(0,0,0,0.15));
        max-height: 90vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      .export-content-large {
        width: 850px;
        max-width: 95vw;
      }

      .export-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--spacing-md, 12px) var(--spacing-lg, 16px);
        border-bottom: 1px solid var(--border-color, #e0e0e0);
      }

      .export-header h3 {
        margin: 0;
        font-size: var(--font-size-lg, 16px);
      }

      .export-close {
        background: none;
        border: none;
        font-size: 24px;
        cursor: pointer;
        color: var(--text-secondary, #666);
      }

      .export-body-grid {
        display: grid;
        /* minmax(0, 1fr): 자식 min-content(캔버스 명시폭)가 트랙을 밀어내지 못하게 */
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: var(--spacing-lg, 16px);
        padding: var(--spacing-lg, 16px);
        max-height: 65vh;
        overflow-y: auto;
      }

      .export-options {
        overflow-y: auto;
        max-height: 100%;
        padding-right: var(--spacing-sm, 8px);
      }

      .export-section {
        margin-bottom: var(--spacing-md, 12px);
      }

      .export-section h4 {
        font-size: var(--font-size-sm, 13px);
        font-weight: 600;
        color: var(--text-primary, #333);
        margin-bottom: var(--spacing-sm, 8px);
        padding-bottom: var(--spacing-xs, 4px);
        border-bottom: 1px solid var(--border-color, #e0e0e0);
        display: flex;
        align-items: center;
        gap: var(--spacing-sm, 8px);
      }

      .help-text {
        font-size: var(--font-size-xs, 11px);
        font-weight: normal;
        color: var(--text-secondary, #666);
      }

      .export-form-group {
        margin-bottom: var(--spacing-sm, 8px);
      }

      .export-form-group label {
        display: block;
        font-size: var(--font-size-sm, 13px);
        margin-bottom: 4px;
      }

      .export-form-group input,
      .export-form-group select {
        width: 100%;
        padding: var(--spacing-xs, 4px) var(--spacing-sm, 8px);
        border: 1px solid var(--border-color, #e0e0e0);
        border-radius: var(--radius-sm, 4px);
        font-size: var(--font-size-sm, 13px);
      }

      .basemap-toggle-group {
        background: var(--bg-tertiary, #f5f5f5);
        border: 1px solid var(--border-color, #e0e0e0);
        border-radius: var(--radius-sm, 4px);
        padding: var(--spacing-sm, 8px);
      }

      .basemap-toggle-group label.basemap-toggle {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm, 8px);
        cursor: pointer;
        font-size: var(--font-size-sm, 13px);
        font-weight: 500;
        margin-bottom: 4px;
      }

      .basemap-toggle-group .basemap-toggle input[type="checkbox"] {
        width: auto;
        margin: 0;
      }

      .basemap-toggle-group .basemap-hint {
        margin: 0 0 0 22px;
        font-size: var(--font-size-xs, 11px);
        color: var(--text-secondary, #666);
        line-height: 1.4;
      }

      .export-element-group {
        margin-bottom: var(--spacing-sm, 8px);
        border: 1px solid var(--border-color, #e0e0e0);
        border-radius: var(--radius-sm, 4px);
        padding: var(--spacing-sm, 8px);
      }

      .export-toggle {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm, 8px);
        cursor: pointer;
        font-size: var(--font-size-sm, 13px);
        font-weight: 500;
      }

      .element-options {
        margin-top: var(--spacing-sm, 8px);
        padding: var(--spacing-sm, 8px);
        background: var(--bg-tertiary, #f5f5f5);
        border-radius: var(--radius-sm, 4px);
      }

      .element-options input[type="text"],
      .element-options textarea {
        width: 100%;
        padding: var(--spacing-xs, 4px) var(--spacing-sm, 8px);
        border: 1px solid var(--border-color, #e0e0e0);
        border-radius: var(--radius-sm, 4px);
        font-size: var(--font-size-sm, 13px);
        margin-bottom: var(--spacing-xs, 4px);
      }

      .style-options {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xs, 4px);
      }

      .style-row {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm, 8px);
        font-size: var(--font-size-xs, 11px);
      }

      .style-row > label:first-child {
        width: 45px;
        flex-shrink: 0;
      }

      .style-row select,
      .style-row input[type="number"] {
        flex: 1;
        padding: 2px 4px;
        border: 1px solid var(--border-color, #e0e0e0);
        border-radius: var(--radius-sm, 4px);
        font-size: var(--font-size-xs, 11px);
      }

      .style-row input[type="color"] {
        width: 30px;
        height: 22px;
        padding: 0;
        border: 1px solid var(--border-color, #e0e0e0);
        border-radius: var(--radius-sm, 4px);
        cursor: pointer;
      }

      .checkbox-inline {
        display: flex;
        align-items: center;
        gap: 4px;
        cursor: pointer;
        white-space: nowrap;
      }

      .legend-layers {
        max-height: 100px;
        overflow-y: auto;
        margin-bottom: var(--spacing-sm, 8px);
      }

      .legend-layer-item {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm, 8px);
        padding: 2px 0;
        font-size: var(--font-size-xs, 11px);
        cursor: pointer;
      }

      .layer-color {
        width: 12px;
        height: 12px;
        border-radius: 2px;
        border: 1px solid var(--border-color, #e0e0e0);
      }

      .export-preview-container {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm, 8px);
      }

      .export-preview-container h4 {
        font-size: var(--font-size-sm, 13px);
        font-weight: 600;
        margin: 0;
        display: flex;
        align-items: center;
        gap: var(--spacing-sm, 8px);
      }

      .export-preview {
        border: 1px solid var(--border-color, #e0e0e0);
        border-radius: var(--radius-sm, 4px);
        background: var(--bg-tertiary, #f5f5f5);
        min-height: 220px;
        max-height: 480px;
        overflow: auto;
        display: block;
        position: relative;
      }

      .export-preview canvas {
        display: block;
        margin: 0 auto;
      }

      .preview-actions {
        display: flex;
        gap: var(--spacing-sm, 8px);
        align-items: center;
      }

      #preview-zoom-value {
        font-size: 11px;
        color: var(--text-secondary, #666);
        min-width: 36px;
        text-align: center;
      }

      .export-footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-sm, 8px);
        padding: var(--spacing-md, 12px) var(--spacing-lg, 16px);
        border-top: 1px solid var(--border-color, #e0e0e0);
      }

      .no-layers {
        color: var(--text-secondary, #666);
        font-size: var(--font-size-xs, 11px);
        margin: 0;
      }
    `;
    document.head.appendChild(styles);
  }

  close() {
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
    }
  }
}

export const exportPanel = new ExportPanel();
